import { z } from 'zod';
import { BelowMinimumError, ValidationError } from './errors.js';
import { applySizeUpcharges, getEmbAdditionalLocationRate, getEmbBaseRate, getEmbDigitizingCost, getEmbNameRate, getEmbNumberRate, round2, } from './helpers.js';
import { DEFAULT_PRICING_CONFIG_BODY } from './pricing-config/defaults.js';
const SizesSchema = z
    .object({
    sxl: z.number().int().min(0),
    xxl: z.number().int().min(0),
    xxxl: z.number().int().min(0),
    xxxxl: z.number().int().min(0).default(0),
    xxxxxl: z.number().int().min(0).default(0),
})
    .refine((s) => s.sxl + s.xxl + s.xxxl + s.xxxxl + s.xxxxxl > 0, {
    message: 'Total quantity across sizes must be greater than zero.',
});
const GarmentSchema = z.discriminatedUnion('source', [
    z.object({
        source: z.literal('we_provide'),
        blankCostEach: z.number().min(0),
    }),
    z.object({
        source: z.literal('customer_provides'),
    }),
]);
export const QuoteEmbroideryInputSchema = z.object({
    sizes: SizesSchema,
    placement: z.enum(['lc', 'hat', 'back']),
    stitchComplexity: z.enum(['simple', 'standard', 'complex']),
    additionalLocations: z.array(z.enum(['sleeve', 'rightChest', 'yoke'])).max(3),
    personalization: z.object({
        name: z.boolean(),
        number: z.boolean(),
    }),
    digitizing: z.object({
        type: z.enum(['none', 'dst', 'lc', 'hat', 'hat3d', 'beanie', 'bigfb', 'lchat', 'combo3']),
        rush: z.boolean(),
    }),
    garment: GarmentSchema,
});
export const QuoteEmbroideryResultSchema = z
    .object({
    totalQty: z.number().int(),
    qtyTier: z.union([z.literal(12), z.literal(24), z.literal(48), z.literal(72), z.literal(144)]),
    baseRatePerUnit: z.number(),
    additionalLocationChargePerUnit: z.number(),
    personalizationChargePerUnit: z.number(),
    embPerUnit: z.number(),
    blankCostPerUnit: z.number(),
    sellPerUnit: z.number(),
    internalEmbCostPerUnit: z.number(),
    costPerUnit: z.number(),
    sizeUpchargeTotal: z.number(),
    digitizingInternalCost: z.number(),
    digitizingCustomerCharge: z.number(),
    orderCost: z.number(),
    orderTotal: z.number(),
    profit: z.number(),
    marginPct: z.number(),
})
    .meta({
    id: 'QuoteEmbroideryResult',
    description: 'Embroidery quote output. `qtyTier` is the discrete pricing tier used (12, 24, 48, 72, or 144).',
});
function resolveTier(qty) {
    if (qty >= 144)
        return 144;
    if (qty >= 72)
        return 72;
    if (qty >= 48)
        return 48;
    if (qty >= 24)
        return 24;
    return 12;
}
function getRepresentativeStitchK(complexity, stitchTiers) {
    if (complexity === 'simple')
        return stitchTiers.simpleRepresentativeK;
    if (complexity === 'standard')
        return stitchTiers.standardRepresentativeK;
    return stitchTiers.complexRepresentativeK;
}
/**
 * v16 calcEMB: tier-based per-unit embroidery rate with stitch-complexity multiplier,
 * plus additional placements, personalization (name/number), and one-time digitizing.
 *
 * - Rates use Math.round after complexity multiplier (v16 parity).
 * - Digitizing: customer pays myCost + $10 markup; one-time fee on the order.
 * - Size upcharges are order-level (not amortized per-unit), pass-through at cost.
 * - Minimum qty 12 → BelowMinimumError.
 */
export function quoteEmbroidery(rawInput, config) {
    const parsed = QuoteEmbroideryInputSchema.safeParse(rawInput);
    if (!parsed.success) {
        throw new ValidationError(parsed.error.issues);
    }
    const input = parsed.data;
    const cfg = config ?? DEFAULT_PRICING_CONFIG_BODY;
    const emb = cfg.embroidery;
    const sizeUpcharge = cfg.universal.sizeUpcharge;
    const sizeAgg = applySizeUpcharges(input.sizes, sizeUpcharge);
    const totalQty = sizeAgg.totalQty;
    if (totalQty < emb.minQty) {
        throw new BelowMinimumError('embroidery', totalQty, emb.minQty);
    }
    const qtyTier = resolveTier(totalQty);
    const baseRate = getEmbBaseRate(input.placement, totalQty, input.stitchComplexity, emb.baseTiers, emb.complexityMultiplier);
    const addlRate = getEmbAdditionalLocationRate(totalQty, emb.additionalLocationTiers);
    const numAddl = input.additionalLocations.length;
    const addlCharge = numAddl * addlRate;
    const nameRate = getEmbNameRate(totalQty, emb.nameTiers);
    const numberRate = getEmbNumberRate(totalQty, emb);
    const personalizationCharge = (input.personalization.name ? nameRate : 0) + (input.personalization.number ? numberRate : 0);
    const embPerUnit = baseRate + addlCharge + personalizationCharge;
    const blankCostEach = input.garment.source === 'we_provide' ? input.garment.blankCostEach : 0;
    const sellPerUnit = embPerUnit + blankCostEach;
    const stitchK = getRepresentativeStitchK(input.stitchComplexity, emb.stitchTiers);
    const internalEmbCostPerUnit = stitchK * emb.costDefaults.costPerThousandStitches +
        emb.costDefaults.overheadPerPiece +
        emb.costDefaults.threadPerPiece;
    const costPerUnit = internalEmbCostPerUnit + blankCostEach;
    const digi = getEmbDigitizingCost(input.digitizing.type, input.digitizing.rush, emb);
    const orderTotal = sellPerUnit * totalQty + sizeAgg.totalUpcharge + digi.customerCharge;
    const orderCost = costPerUnit * totalQty + sizeAgg.totalUpcharge + digi.myCost;
    const profit = orderTotal - orderCost;
    const marginPct = sellPerUnit > 0 ? ((sellPerUnit - costPerUnit) / sellPerUnit) * 100 : 0;
    return {
        totalQty,
        qtyTier,
        baseRatePerUnit: baseRate,
        additionalLocationChargePerUnit: round2(addlCharge),
        personalizationChargePerUnit: round2(personalizationCharge),
        embPerUnit: round2(embPerUnit),
        blankCostPerUnit: blankCostEach,
        sellPerUnit: round2(sellPerUnit),
        internalEmbCostPerUnit: round2(internalEmbCostPerUnit),
        costPerUnit: round2(costPerUnit),
        sizeUpchargeTotal: sizeAgg.totalUpcharge,
        digitizingInternalCost: digi.myCost,
        digitizingCustomerCharge: digi.customerCharge,
        orderCost: round2(orderCost),
        orderTotal: round2(orderTotal),
        profit: round2(profit),
        marginPct: round2(marginPct),
    };
}
//# sourceMappingURL=quote-embroidery.js.map